import { ReactNode, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Header } from './Header'
import { NavBar } from './NavBar'
import { Button } from '../ui/button'
import { useCanGoBack } from '@shared/hooks'
import { actionStorage } from '@shared/lib/storage'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const canGoBack = useCanGoBack()

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // Don't show back button on home page
  const isHomePage = location.pathname === '/'
  const shouldShowBack = canGoBack && !isHomePage

  const handleBackClick = () => {
    // Check if we're on profile page with a pending action return path
    const pendingAction = actionStorage.getPendingAction()

    if (location.pathname === '/profile' && pendingAction?.returnPath) {
      // Use stored return path from validation flow
      void navigate(pendingAction.returnPath)
      return
    }

    // Check if we have history to go back to
    if (canGoBack) {
      void navigate(-1)
    } else {
      // No history, go home
      void navigate('/')
    }
  }

  // Routes that don't show layout (auth pages)
  const noLayoutRoutes = ['/register', '/login', '/passwordrecovery', '/resetPassword', '/recruit']
  const isAuthRoute = noLayoutRoutes.includes(location.pathname.split('&')[0] || location.pathname)

  if (isAuthRoute) {
    return <>{children}</>
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center bg-muted/30">
      {/* Header */}
      <Header />

      {/* Main content wrapper */}
      <div className="flex flex-col mx-auto w-full max-w-md bg-background">
        {/* Sub-header: Back Button Zone (always reserves space) */}
        <div className="w-full min-h-[52px] px-4 pt-3 pb-2 bg-transparent">
          {shouldShowBack && (
            <Button
              variant="ghost"
              size="default"
              onClick={handleBackClick}
              className="gap-2 -ml-2"
              aria-label="Volver a la página anterior"
            >
              <ArrowLeft size={18} />
              <span className="text-sm">Volver</span>
            </Button>
          )}
        </div>

        {/* Main Content */}
        <main className="flex-1 pb-16">{children}</main>
      </div>

      {/* Bottom Navigation */}
      <NavBar />
    </div>
  )
}
